
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DemoEvent, DemoEventInput } from "@/types/demo-calendar";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/utils/tracking";

export const useDemoEvents = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: events = [], isLoading, error } = useQuery<DemoEvent[]>({
    queryKey: ['demo-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('demo_calendar')
        .select('*')
        .order('event_date', { ascending: true });

      if (error) throw error;
      return data as DemoEvent[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const createEventMutation = useMutation<DemoEvent, Error, DemoEventInput>({
    mutationFn: async (eventData: DemoEventInput) => {
      const { data, error } = await supabase
        .from('demo_calendar')
        .insert([eventData])
        .select()
        .single();

      if (error) throw error;
      return data as DemoEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-events'] });
      trackEvent("demo_event_create_succeeded", {
        surface: "demo_calendar",
      });
      toast({
        title: "Event created",
        description: "Your demo event has been successfully created.",
      });
    },
    onError: (error) => {
      console.error('Error creating event:', error);
      trackEvent("demo_event_create_failed", {
        failure_reason: error.message,
      });
      toast({
        title: "Error creating event",
        description: "There was a problem creating your event. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateEventMutation = useMutation<
    DemoEvent,
    Error,
    { id: string; eventData: Partial<DemoEventInput> }
  >({
    mutationFn: async ({ id, eventData }: { id: string; eventData: Partial<DemoEventInput> }) => {
      const { data, error } = await supabase
        .from('demo_calendar')
        .update(eventData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as DemoEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-events'] });
      toast({
        title: "Event updated",
        description: "Your demo event has been successfully updated.",
      });
    },
    onError: (error) => {
      console.error('Error updating event:', error);
      trackEvent("demo_event_update_failed", {
        failure_reason: error.message,
      });
      toast({
        title: "Error updating event",
        description: "There was a problem updating your event. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteEventMutation = useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('demo_calendar')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demo-events'] });
      toast({
        title: "Event deleted",
        description: "Your demo event has been successfully deleted.",
      });
    },
    onError: (error) => {
      console.error('Error deleting event:', error);
      trackEvent("demo_event_delete_failed", {
        failure_reason: error.message,
      });
      toast({
        title: "Error deleting event",
        description: "There was a problem deleting your event. Please try again.",
        variant: "destructive",
      });
    },
  });

  return {
    events,
    isLoading,
    error,
    createEvent: createEventMutation.mutate,
    createEventAsync: createEventMutation.mutateAsync,
    updateEvent: updateEventMutation.mutate,
    updateEventAsync: updateEventMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutate,
    deleteEventAsync: deleteEventMutation.mutateAsync,
    isCreating: createEventMutation.isPending,
    isUpdating: updateEventMutation.isPending,
    isDeleting: deleteEventMutation.isPending,
  };
};
